'use client'

import React from 'react'

import { THEME_COLORS_ENUM } from '../../../../components/button'
import { cn } from '../../../../utils/cn'
import { ORDER_TERMS_BASE } from '../_constants'

import { AModulesAccordion } from './a-modules-accordion'
import { AModulesOrderButton } from './a-modules-order-button'

import type { IProgram } from '../../../../components/program-card'
import type { IProgramModule } from '../../../../types/module'
import type { IAnnualProgramConfig, IModulesSection } from '../_types/types'

export interface IAAnnualProgramPreviewModulesProps {
	modulesSection: IModulesSection
	currentProgram: IProgram
	currentProgramConfig?: IAnnualProgramConfig
	programModules: IProgramModule[]
	selectedModules: IProgramModule[]
	onModulesChange: (modules: IProgramModule[]) => void
	link?: React.ElementType<React.AnchorHTMLAttributes<HTMLAnchorElement>>
	renderSwitcher: () => React.ReactNode
	/** When false, program switcher buttons are hidden in this section. Default: true when multiple programs. */
	showProgramsSwitch: boolean
	themeClass: string
}

export const AAnnualProgramPreviewModules: React.FC<IAAnnualProgramPreviewModulesProps> = ({
	modulesSection,
	currentProgram,
	currentProgramConfig,
	programModules,
	selectedModules,
	onModulesChange,
	link,
	renderSwitcher,
	showProgramsSwitch,
	themeClass
}) => {
	const isTextMode =
		currentProgramConfig?.modulesType === 'text' && Boolean(currentProgramConfig?.modulesText)
	const currentModulesText = currentProgramConfig?.modulesText

	return (
		<section
			id='moduly'
			className={cn(
				'm-annual-program-preview__modules-section',
				themeClass && `m-annual-program-preview__modules-section--${themeClass}`
			)}
		>
			<div className='m-annual-program-preview__modules-wrapper'>
				<div className='m-annual-program-preview__modules-header'>
					<h2
						className='m-annual-program-preview__modules-header__title'
						dangerouslySetInnerHTML={{
							__html: modulesSection.title
						}}
					/>
					{modulesSection.text && (
						<p
							className='m-annual-program-preview__modules-header__text'
							dangerouslySetInnerHTML={{
								__html: modulesSection.text
							}}
						/>
					)}
					{showProgramsSwitch && renderSwitcher()}
				</div>

				<div className='m-annual-program-preview__modules-content'>
					<div className='m-annual-program-preview__modules-content__left'>
						<div className='m-annual-program-preview__modules-card'>
							{isTextMode ? (
								<>
									<div
										className='m-annual-program-preview__modules-text'
										dangerouslySetInnerHTML={{
											__html: currentModulesText!
										}}
									/>
									<AModulesOrderButton
										baseUrl={ORDER_TERMS_BASE}
										programId={currentProgram.id}
										programPrice={currentProgram.price}
										programPriceOld={currentProgram.oldPrice}
										selectedModules={selectedModules}
										color={currentProgram.color as THEME_COLORS_ENUM}
										link={link}
									/>
								</>
							) : (
								<>
									<AModulesAccordion
										modules={programModules}
										selectedModules={selectedModules}
										onModulesChange={onModulesChange}
										initialExpanded={true}
									/>
									<AModulesOrderButton
										baseUrl={ORDER_TERMS_BASE}
										programId={currentProgram.id}
										programPrice={currentProgram.price}
										programPriceOld={currentProgram.oldPrice}
										selectedModules={selectedModules}
										color={currentProgram.color as THEME_COLORS_ENUM}
										link={link}
									/>
								</>
							)}
						</div>
					</div>

					{modulesSection.image && (
						<div className='m-annual-program-preview__modules-content__right'>
							<div className='m-annual-program-preview__modules-content__image'>
								<img
									src={modulesSection.image.src}
									alt={modulesSection.image.alt}
								/>
							</div>
						</div>
					)}
				</div>

				{modulesSection.footerText && (
					<div
						className='m-annual-program-preview__modules-footer'
						dangerouslySetInnerHTML={{
							__html: modulesSection.footerText
						}}
					/>
				)}
			</div>
		</section>
	)
}

AAnnualProgramPreviewModules.displayName = 'AAnnualProgramPreviewModules'
